import React from "react"

import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import { t, Trans } from "@lingui/macro"

import { Floor, FloorType } from "models"

interface ManageFloorsModalProps {
    open: boolean
    floors: Floor[]
    onClose: () => void
    onSave: (floors: Floor[]) => void
    isSaving?: boolean
}

const FLOOR_TYPE_OPTIONS = Object.values(FloorType) as FloorType[]

const createEmptyFloor = (): Floor => ({
    floorType: FloorType.FLOOR,
    surface: 0,
    floorNumber: null,
})

const cloneFloors = (source?: Floor[]): Floor[] => {
    if (!source || source.length === 0) {
        return [createEmptyFloor()]
    }
    return source.map((floor) => ({ ...floor }))
}

const getFloorTypeLabel = (type: FloorType): string => {
    switch (type) {
        case FloorType.MEZZANINE:
            return t({ id: "asset-details.floors.type.mezzanine", message: "Soppalco" })
        case FloorType.FLOOR:
        default:
            return t({ id: "asset-details.floors.type.floor", message: "Piano" })
    }
}

export const ManageFloorsModal: React.FC<ManageFloorsModalProps> = ({
    open,
    floors,
    onClose,
    onSave,
    isSaving = false,
}) => {
    const [draftFloors, setDraftFloors] = React.useState<Floor[]>(cloneFloors(floors))

    React.useEffect(() => {
        setDraftFloors(cloneFloors(floors))
    }, [floors, open])

    const handleFieldChange = <K extends keyof Floor>(index: number, field: K, value: Floor[K]) => {
        setDraftFloors((prev) =>
            prev.map((floor, i) => (i === index ? { ...floor, [field]: value } : floor))
        )
    }

    const handleFloorTypeChange = (index: number, value: FloorType) => {
        handleFieldChange(index, "floorType", value)
    }

    const handleSurfaceChange = (index: number, value: string) => {
        const parsedValue = value === "" ? 0 : Number(value)
        handleFieldChange(index, "surface", Number.isFinite(parsedValue) ? parsedValue : 0)
    }

    const handleFloorNumberChange = (index: number, value: string) => {
        const parsedValue = value === "" ? null : Number(value)
        handleFieldChange(index, "floorNumber", parsedValue)
    }

    const handleRemoveFloor = (index: number) => {
        setDraftFloors((prev) => {
            if (prev.length === 1) {
                return [createEmptyFloor()]
            }
            return prev.filter((_, i) => i !== index)
        })
    }

    const handleAddFloor = () => {
        setDraftFloors((prev) => [...prev, createEmptyFloor()])
    }

    const handleSave = () => {
        const sanitizedFloors = draftFloors.map((floor) => ({
            id: floor.id,
            floorType: floor.floorType ?? FloorType.FLOOR,
            surface: Number.isFinite(floor.surface) ? floor.surface : 0,
            floorNumber: floor.floorNumber === null || floor.floorNumber === undefined || Number.isNaN(floor.floorNumber)
                ? null
                : Number(floor.floorNumber),
        }))
        onSave(sanitizedFloors)
    }

    const floorSummary = draftFloors.reduce<Record<FloorType, number>>((acc, floor) => {
        const type = floor.floorType ?? FloorType.FLOOR
        acc[type] = (acc[type] ?? 0) + 1
        return acc
    }, {} as Record<FloorType, number>)

    const totalFloors = draftFloors.length

    const isSaveDisabled = isSaving || draftFloors.some((floor) => !floor.floorType)

    const handleDialogClose = (_event?: unknown, reason?: string) => {
        if (isSaving) {
            return
        }

        if (reason === "backdropClick" || reason === "escapeKeyDown") {
            onClose()
            return
        }

        onClose()
    }

    return (
        <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Trans id="asset-details.floors.manage-title">Gestisci piani</Trans>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                            <Trans id="asset-details.floors.total">
                                Totale piani: {totalFloors}
                            </Trans>
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                            {FLOOR_TYPE_OPTIONS.map((type) => (
                                <Chip
                                    key={type}
                                    label={`${getFloorTypeLabel(type)}: ${floorSummary[type] ?? 0}`}
                                    size="small"
                                />
                            ))}
                        </Stack>
                    </Box>
                    <Stack spacing={2}>
                        {draftFloors.map((floor, index) => (
                            <Box key={`${floor.id ?? "new"}-${index}`} sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2 }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            select
                                            fullWidth
                                            label={t({ id: "asset-details.floors.fields.type", message: "Tipo" })}
                                            value={floor.floorType ?? FloorType.FLOOR}
                                            onChange={(event) => handleFloorTypeChange(index, event.target.value as FloorType)}
                                            disabled={isSaving}
                                            size="small"
                                        >
                                            {FLOOR_TYPE_OPTIONS.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {getFloorTypeLabel(type)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            type="number"
                                            fullWidth
                                            label={t({ id: "asset-details.floors.fields.number", message: "Numero piano" })}
                                            value={floor.floorNumber ?? ""}
                                            onChange={(event) => handleFloorNumberChange(index, event.target.value)}
                                            disabled={isSaving}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            type="number"
                                            fullWidth
                                            label={t({ id: "asset-details.floors.fields.surface", message: "Superficie (mq)" })}
                                            value={floor.surface ?? 0}
                                            onChange={(event) => handleSurfaceChange(index, event.target.value)}
                                            disabled={isSaving}
                                            size="small"
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={1}>
                                        <IconButton
                                            aria-label={t({ id: "asset-details.floors.actions.remove", message: "Rimuovi piano" })}
                                            onClick={() => handleRemoveFloor(index)}
                                            disabled={isSaving}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        ))}
                    </Stack>
                    <Box>
                        <Button
                            variant="outlined"
                            onClick={handleAddFloor}
                            startIcon={<AddCircleOutlineIcon />}
                            disabled={isSaving}
                        >
                            <Trans id="asset-details.floors.actions.add">Aggiungi piano</Trans>
                        </Button>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="neutral" variant="text" disabled={isSaving}>
                    <Trans id="asset-details.floors.actions.cancel">Annulla</Trans>
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={isSaveDisabled}>
                    {isSaving ? t({ id: "asset-details.floors.actions.saving", message: "Salvataggio..." }) : t({ id: "asset-details.floors.actions.save", message: "Salva" })}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
